import { decryptLocalStorageMobileNo } from "./crypto";

declare namespace newrelic {
  type SimpleType = string | number | boolean | null | Error;
  function noticeError(error: string | Error, errorDetailsObj?: Record<string, SimpleType>): void;
  function setCustomAttribute(attribute: string, value: SimpleType, persist?: boolean): void;
  function addPageAction(actionName: string, eventDetailsObj?: Record<string, SimpleType>): void;
}

export const logErrorToNewRelic = (
  error: string | Error,
  errorDetailsObj?: Record<string, newrelic.SimpleType> | undefined
) => {
  if (typeof window === "undefined") return;
  if ((window as unknown as { newrelic?: unknown }).newrelic)
    newrelic?.noticeError(error, errorDetailsObj);
};

export const logCustomAttributeToNewRelic = (
  attribute: string,
  value: newrelic.SimpleType,
  persist?: boolean
) => {
  if (typeof window === "undefined") return;
  if ((window as unknown as { newrelic?: unknown }).newrelic)
    newrelic?.setCustomAttribute(attribute, value, persist);
};

export const logPageActionToNewRelic = (
  actionName: string,
  eventDetailsObj?: Record<string, newrelic.SimpleType> | undefined
) => {
  if (typeof window === "undefined") return;
  if ((window as unknown as { newrelic?: unknown }).newrelic)
    newrelic?.addPageAction(actionName, eventDetailsObj);
};

/**
 * Logs a New Relic custom attribute enriched with the current page URL.
 * The value object is JSON-stringified into a single string attribute,
 * matching the pattern used in bajajfinserv-web (3in1cms).
 *
 * Use this for user-interaction events (button clicks, downloads, etc.)
 * so all contextual fields are captured together in one NR attribute.
 */
export const logCustomAttributeToNewRelicWithAppData = (
  attribute: string,
  value: Record<string, string | null | undefined>,
  persist?: boolean
) => {
  if (typeof window === "undefined") return;
  const nrLoaded = !!(window as unknown as { newrelic?: unknown }).newrelic;
  if (!nrLoaded) return;
  logCustomAttributeToNewRelic(
    attribute,
    JSON.stringify({
      url: window.location.href,
      ...value,
    }),
    persist
  );
};

/**
 * Resolves the mobile number from localStorage and returns it base64-encoded.
 * Handles both numeric (plaintext) and encrypted storage formats.
 * Returns empty string if unavailable or on error.
 */
export const getEncryptedMobileNumber = async (): Promise<string> => {
  if (typeof window === "undefined") return "";
  const encryptedMobile = localStorage.getItem("mobileNumber") ?? "";
  if (!encryptedMobile) return "";
  try {
    const mobileNumber = Number.isNaN(Number(encryptedMobile))
      ? await decryptLocalStorageMobileNo(encryptedMobile)
      : encryptedMobile;
    return mobileNumber ? btoa(String(mobileNumber)) : "";
  } catch {
    return "";
  }
};
